import { Textarea } from "../componentes/Textarea"
import { NativeSelect } from "../componentes/NativeSelect"
import { Escolha } from "../componentes/Escolha"
import { Input } from "../componentes/Input"
import { useCallback, useEffect, useState } from "react"

import {
  api,
  type AlcanceDeConfiguracoes,
  type EntidadeNaLista,
  type FaixaNaTela,
  type RegraNaTela,
  type RespostaDasFaixas,
  type RespostaDasRegras,
  type RespostaDosTipos,
  type TipoNaTela,
} from "../api"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Skeleton } from "../componentes/Skeleton"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import * as Icones from "../componentes/Icones"
import { Selo } from "../componentes/Selo"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../componentes/Table"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { data, inteiro, moeda, nivelDeAssinatura } from "../formato"
import {
  BOTAO_BRANCO,
  BOTAO_CANCELAR,
  CABECALHO_DO_SHEET,
  MAIUSCULA,
  MAIUSCULA_INLINE,
  RECUSA,
  ROTULO_DE_CAMPO,
} from "../lib/classes"
import { cn } from "../lib/cn"


const HOJE = () => new Date().toISOString().slice(0, 10)

function amanha(): string {
  const dia = new Date()
  dia.setDate(dia.getDate() + 1)
  return dia.toISOString().slice(0, 10)
}

const PALAVRA_DO_VALOR: Record<string, string> = {
  publicacao: "publicação",
  assinatura: "assinatura",
  ordem_de_servico: "ordem de serviço",
  avisar: "avisar",
  validada: "validada",
  recusada: "recusada",
  com_ressalva: "com ressalva",
  anotacao: "anotação",
}

function valorComUnidade(valor: string, unidade: string | null, chave = ""): string {
  if (chave === "laudo_nivel_de_assinatura") return nivelDeAssinatura(valor)
  if (unidade === "R$") return moeda(valor)
  const palavra = valor
    .split(",")
    .map((parte) => PALAVRA_DO_VALOR[parte.trim()] ?? parte.trim())
    .join(", ")
  if (unidade === null) return palavra
  return unidade.startsWith("%") ? `${palavra}${unidade}` : `${palavra} ${unidade}`
}

function useEntidadesQueAlcanca(alcance: AlcanceDeConfiguracoes) {
  const [entidades, definirEntidades] = useState<EntidadeNaLista[]>([])
  const soAsFicticias = alcance.conta_de_demonstracao
  useEffect(() => {
    api
      .entidades("")
      .then((resposta) => definirEntidades(resposta.dados.filter((e) => e.is_demo || !soAsFicticias)))
      .catch(() => definirEntidades([]))
  }, [soAsFicticias])
  return entidades
}

const POR_PAGINA_DAS_REGRAS = 25
const FILTRO_DA_BARRA = "w-full min-w-0 md:w-52 md:flex-none"

export function LimitesEPrazos({
  alcance,
  assunto,
}: {
  alcance: AlcanceDeConfiguracoes
  assunto?: string
}) {
  const [resposta, definirResposta] = useState<RespostaDasRegras | null>(null)
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [filtroDeAssunto, definirFiltroDeAssunto] = useState("")
  const [aberta, definirAberta] = useState<string | null>(null)
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA_DAS_REGRAS)
  const [cadastrando, definirCadastrando] = useState<RegraNaTela | null>(null)
  const entidades = useEntidadesQueAlcanca(alcance)

  const carregar = useCallback((termo: string) => {
    definirRecusa(null)
    api
      .regrasDoSistema(termo)
      .then((r) => definirResposta(r))
      .catch((falha: unknown) => definirRecusa(mensagemDeRecusa(falha)))
  }, [])

  useEffect(() => {
    const espera = window.setTimeout(() => carregar(busca), busca === "" ? 0 : 250)
    return () => window.clearTimeout(espera)
  }, [busca, carregar])

  const titulo = assunto === "Senha" ? "Política de senha" : "Limites legais e prazos"
  const dica =
    `${assunto === "Senha" ? "Cada regra da senha" : "Cada regra"} vale por vigência: a nova começa na data marcada, e a anterior da mesma origem termina na véspera. ` +
    "O valor para todas as entidades só o Root altera; cada entidade pode ter o seu."
  const cabecalho = (
    <CardHeader>
      <CardTitle>
        {titulo} <ComDica dica={dica} />
      </CardTitle>
    </CardHeader>
  )

  if (resposta === null) {
    return (
      <Card>
        {cabecalho}
        {recusa !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {recusa}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => carregar(busca)}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const filtro = assunto ?? filtroDeAssunto
  const regras = resposta.dados.filter((r) => filtro === "" || r.assunto === filtro)
  const totalDePaginas = Math.max(1, Math.ceil(regras.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = regras.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)
  const alternar = (regra: RegraNaTela) => definirAberta((atual) => (atual === regra.chave ? null : regra.chave))
  const podeAlgo = (regra: RegraNaTela) =>
    regra.pode_alterar_geral ||
    regra.pode_alterar_da_entidade ||
    (alcance.itens.find((i) => i.chave === "parametros")?.edita === true && entidades.length > 0)

  const vazio = (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icones.Buscar />
        </EmptyMedia>
        <EmptyTitle>Nada encontrado</EmptyTitle>
        <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nenhuma regra neste assunto."}</EmptyDescription>
      </EmptyHeader>
      {busca !== "" && (
        <EmptyContent>
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
            Limpar a busca
          </Button>
        </EmptyContent>
      )}
    </Empty>
  )

  return (
    <Card>
      {cabecalho}
      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={regras.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          aoClicarNaLinha={alternar}
          linhaAberta={(regra) => aberta === regra.chave}
          detalheDaLinha={(regra) => (
            <DetalheDaRegra
              key={regra.chave}
              regra={regra}
              podeAlgo={podeAlgo(regra)}
              podeCriar={opcoesDeAlcanceDaRegra(regra, entidades).length > 0}
              aoNovaVigencia={() => definirCadastrando(regra)}
            />
          )}
          filtrosNaBarra={
            assunto === undefined && (
              <Escolha
                rotulo="Filtrar pelo assunto"
                classeDaCaixa={FILTRO_DA_BARRA}
                valor={filtroDeAssunto}
                aoMudar={(valor) => {
                  definirFiltroDeAssunto(valor)
                  definirPagina(1)
                }}
                vazio="Todos os assuntos"
                placeholderDaBusca="Buscar pelo assunto"
                opcoes={resposta.assuntos.map((a) => ({ valor: a, rotulo: a }))}
              />
            )
          }
        >
          {assunto === undefined && (
            <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome ou descrição" />
          )}
          <Column<RegraNaTela>
            name="nome"
            label="Regra"
            enableSorting={false}
            render={(_valor, regra) => (
              <span className="flex min-w-[220px] items-center gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-expanded={aberta === regra.chave}
                  aria-label={aberta === regra.chave ? `Esconder o detalhe de ${regra.nome}` : `Mostrar o detalhe de ${regra.nome}`}
                  title={aberta === regra.chave ? "Esconde o fundamento, o histórico e a nova vigência" : "Mostra o fundamento, o histórico e a nova vigência"}
                  onClick={() => alternar(regra)}
                >
                  <span className={cn("flex transition-transform motion-reduce:transition-none", aberta === regra.chave && "rotate-90")}>
                    <Icones.Proxima tamanho={16} />
                  </span>
                </Button>
                <span className="font-medium">{regra.nome}</span>
              </span>
            )}
          />
          {assunto === undefined && <Column<RegraNaTela> name="assunto" label="Assunto" enableSorting={false} />}
          <Column<RegraNaTela>
            name="vigente"
            label="Valor que vale hoje"
            enableSorting={false}
            render={(_valor, regra) => (regra.vigente === null ? "—" : valorComUnidade(regra.vigente.valor, regra.unidade, regra.chave))}
          />
          <Column<RegraNaTela>
            name="comportamento"
            label="Quando não se cumpre"
            enableSorting={false}
            render={(_valor, regra) => regra.vigente?.comportamento_em_palavras ?? "—"}
          />
          <Column<RegraNaTela>
            name="vale_para"
            label="Vale para"
            enableSorting={false}
            render={(_valor, regra) => regra.vigente?.vale_para ?? "Não definido"}
          />
          <Column<RegraNaTela>
            name="desde"
            label="Desde"
            enableSorting={false}
            render={(_valor, regra) => (regra.vigente === null ? "—" : data(regra.vigente.vigencia_inicio))}
          />
        </TablePrincipal2>
      </CardContent>

      <Sheet open={cadastrando !== null} onOpenChange={(aberta) => !aberta && definirCadastrando(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {cadastrando !== null ? `Nova vigência: ${cadastrando.nome}` : "Nova vigência"}
            </SheetTitle>
          </SheetHeader>
          {cadastrando !== null && (
            <FormularioDaVigencia
              key={cadastrando.chave}
              regra={cadastrando}
              entidades={entidades}
              aoCancelar={() => definirCadastrando(null)}
              aoSalvar={(frase) => {
                toast.success(`${cadastrando.nome}: nova vigência salva`, { description: frase })
                definirCadastrando(null)
                carregar(busca)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

const COMPORTAMENTO_EM_PALAVRAS: Record<string, string> = {
  bloqueio: "Impede",
  alerta: "Avisa",
  registro: "Só registra",
}

export function DetalheDaRegra({
  regra,
  podeAlgo,
  podeCriar,
  aoNovaVigencia,
}: {
  regra: RegraNaTela
  podeAlgo: boolean
  podeCriar: boolean
  aoNovaVigencia: () => void
}) {
  const vigente = regra.vigente
  return (
    <div className="flex flex-col gap-3 text-sm text-(--tinta)">
      {vigente?.descricao && <p className={cn("m-0 leading-relaxed", MAIUSCULA)}>{vigente.descricao}</p>}
      <p className="m-0 leading-relaxed">
        <strong className="font-semibold">Fundamento: </strong>
        {vigente?.fundamento ?? "Sem fundamento escrito"}
      </p>

      <div className="overflow-x-auto rounded-md border border-(--borda) bg-(--branco)">
        <Table className="text-sm">
          <TableHeader className="bg-muted">
            <TableRow className="hover:bg-transparent">
              <TableHead colSpan={6} className="h-9 px-3 text-left text-xs font-semibold tracking-wide text-(--tinta) uppercase">
                Histórico
              </TableHead>
            </TableRow>
            <TableRow className="hover:bg-transparent">
              {["Valor", "Quando não se cumpre", "Vale para", "De", "Até", "Hoje"].map((titulo) => (
                <TableHead key={titulo} className="h-9 px-3 text-left text-xs font-semibold text-(--tinta-2)">
                  {titulo}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {regra.historico.map((linha) => (
              <TableRow key={linha.id} className="hover:bg-transparent">
                <TableCell className="px-3 py-2 text-(--tinta)">{valorComUnidade(linha.valor, regra.unidade, regra.chave)}</TableCell>
                <TableCell className={cn("px-3 py-2 text-(--tinta)", MAIUSCULA)}>{linha.comportamento_em_palavras}</TableCell>
                <TableCell className={cn("px-3 py-2 text-(--tinta)", MAIUSCULA)}>{linha.vale_para}</TableCell>
                <TableCell className="px-3 py-2 text-(--tinta)">{data(linha.vigencia_inicio)}</TableCell>
                <TableCell className="px-3 py-2 text-(--tinta)">{linha.vigencia_fim === null ? "Sem término" : data(linha.vigencia_fim)}</TableCell>
                <TableCell className="px-3 py-2 text-(--tinta)">
                  {linha.vale_hoje ? "Vale" : linha.vigencia_inicio > HOJE() ? "Ainda não começou" : "Encerrada"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {!podeAlgo || !podeCriar ? (
        <p className="m-0 text-(--tinta-2)">O que vale para todas as entidades só o Root altera.</p>
      ) : (
        <div className="flex justify-end">
          <Button type="button" onClick={aoNovaVigencia}>
            <Icones.Mais />
            Nova vigência
          </Button>
        </div>
      )}
    </div>
  )
}

export function opcoesDeAlcanceDaRegra(regra: RegraNaTela, entidades: EntidadeNaLista[]) {
  return [
    ...(regra.pode_alterar_geral ? [{ valor: "", rotulo: "Todas as entidades" }] : []),
    ...entidades.map((e) => ({ valor: String(e.id), rotulo: e.sigla })),
  ]
}

export function FormularioDaVigencia({
  regra,
  entidades,
  aoCancelar,
  aoSalvar,
}: {
  regra: RegraNaTela
  entidades: EntidadeNaLista[]
  aoCancelar: () => void
  aoSalvar: (frase: string) => void
}) {
  const vigente = regra.vigente
  const opcoesDeAlcance = opcoesDeAlcanceDaRegra(regra, entidades)
  const [valor, definirValor] = useState(vigente?.valor ?? "")
  const [comportamento, definirComportamento] = useState(vigente?.comportamento ?? "alerta")
  const [inicio, definirInicio] = useState(amanha())
  const [valePara, definirValePara] = useState(opcoesDeAlcance[0]?.valor ?? "")
  const [fundamento, definirFundamento] = useState(vigente?.fundamento ?? "")
  const [confirmando, definirConfirmando] = useState(false)
  const falta = faltaNoFormulario([
    ["o valor", valor],
    ["a data a partir da qual vale", inicio],
  ])

  const salvar = (senha: string) =>
    api
      .novaVigencia(
        regra.chave,
        {
          valor,
          comportamento,
          vigencia_inicio: inicio,
          entidade_id: valePara === "" ? null : Number(valePara),
          fundamento: fundamento.trim() === "" ? null : fundamento,
          descricao: null,
        },
        senha,
      )
      .then((nova) => {
        aoSalvar(
          `${valorComUnidade(nova.valor, regra.unidade, regra.chave)}, ${nova.comportamento_em_palavras}, ` +
            `desde ${data(nova.vigencia_inicio)}, para ${nova.vale_para}. A anterior da mesma origem termina na véspera.`,
        )
      })

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (falta === null && !confirmando) definirConfirmando(true)
      }}
    >
      <div>
        <RotuloDoCampo htmlFor={`valor-${regra.chave}`} obrigatorio>
          Valor{regra.unidade !== null ? ` (${regra.unidade})` : ""}
        </RotuloDoCampo>
        <Input
          id={`valor-${regra.chave}`}
          required
          autoComplete="off"
          value={valor}
          onChange={(e) => definirValor(e.target.value)}
        />
      </div>
      <div>
        <RotuloDoCampo htmlFor={`comportamento-${regra.chave}`} obrigatorio>
          Quando não se cumpre, o sistema
        </RotuloDoCampo>
        <NativeSelect
          id={`comportamento-${regra.chave}`}
          required
          value={comportamento}
          onChange={(e) => definirComportamento(e.target.value)}
        >
          <option value="bloqueio">Impede</option>
          <option value="alerta">Avisa</option>
          <option value="registro">Só registra</option>
        </NativeSelect>
      </div>
      <div>
        <RotuloDoCampo htmlFor={`inicio-${regra.chave}`} obrigatorio>
          Vale a partir de
        </RotuloDoCampo>
        <CampoDeData
          id={`inicio-${regra.chave}`}
          obrigatorio
          valor={inicio}
          aoMudar={(valor) => definirInicio(valor)}
        />
      </div>
      <div>
        <label className={ROTULO_DE_CAMPO} htmlFor={`vale-${regra.chave}`}>
          Vale para
        </label>
        <Escolha
          id={`vale-${regra.chave}`}
          valor={valePara}
          aoMudar={definirValePara}
          placeholderDaBusca="Buscar pela entidade"
          opcoes={opcoesDeAlcance}
        />
      </div>
      <div>
        <label className={ROTULO_DE_CAMPO} htmlFor={`fundamento-${regra.chave}`}>
          Fundamento — o dispositivo ou o ato que sustenta este valor
        </label>
        <Textarea
          id={`fundamento-${regra.chave}`}
          rows={10}
          value={fundamento}
          onChange={(e) => definirFundamento(e.target.value)}
        />
      </div>
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" title={falta ?? undefined} disabled={confirmando || falta !== null}>
          <Icones.Salvar />
          Salvar
        </Button>
      </div>
      <ConfirmarComSenha
        aberto={confirmando}
        titulo={`Nova vigência: ${regra.nome}`}
        resumo={
          <ResumoDoAto
            itens={[
              ["Valor", valorComUnidade(valor, regra.unidade, regra.chave)],
              ["Quando não se cumpre", COMPORTAMENTO_EM_PALAVRAS[comportamento] ?? comportamento],
              ["Vale a partir de", inicio === "" ? "—" : data(inicio)],
              ["Vale para", opcoesDeAlcance.find((o) => o.valor === valePara)?.rotulo ?? "—"],
            ]}
          />
        }
        rotuloDoAto="Salvar"
        iconeDoAto={<Icones.Salvar />}
        aoConfirmar={salvar}
        aoFechar={() => definirConfirmando(false)}
      />
    </form>
  )
}

type AbaDasFaixas = "todas" | "ativas" | "desativadas"

const ABAS_DAS_FAIXAS: DescricaoDeAba<AbaDasFaixas>[] = [
  { chave: "todas", rotulo: "Todas", dica: "Mostra todas as faixas" },
  { chave: "ativas", rotulo: "Ativas", dica: "Mostra só as faixas que geram providências" },
  { chave: "desativadas", rotulo: "Desativadas", dica: "Mostra só as faixas desativadas" },
]

const POR_PAGINA_DAS_FAIXAS = 10

export type GavetaDaFaixa = { modo: "nova" } | { modo: "adaptar"; base: FaixaNaTela } | { modo: "editar"; faixa: FaixaNaTela }

const semAcento = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()

const tituloDaGaveta = (gaveta: GavetaDaFaixa) =>
  gaveta.modo === "nova"
    ? "Nova faixa"
    : gaveta.modo === "adaptar"
      ? `Adaptar a faixa de ${gaveta.base.dias} dias`
      : `Faixa de ${gaveta.faixa.dias} dias`

export function FaixasDeProvidencia({ alcance }: { alcance: AlcanceDeConfiguracoes }) {
  const [resposta, definirResposta] = useState<RespostaDasFaixas | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDasFaixas>("todas")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA_DAS_FAIXAS)
  const [gaveta, definirGaveta] = useState<GavetaDaFaixa | null>(null)
  const [desativando, definirDesativando] = useState<FaixaNaTela | null>(null)
  const entidades = useEntidadesQueAlcanca(alcance)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .faixasDeProvidencia()
      .then(definirResposta)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  const cabecalho = (
    <CardHeader>
      <CardTitle>
        Faixas de providência{" "}
        <ComDica dica="Cada faixa define um prazo antes do fim da vigência e o que deve ser feito. A faixa específica da entidade substitui a geral de mesmo prazo. Se desativada, para de gerar novas providências, e as já geradas continuam nos contratos." />
      </CardTitle>
    </CardHeader>
  )

  if (resposta === null) {
    return (
      <Card>
        {cabecalho}
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const podeCriar = resposta.pode_criar_geral || resposta.pode_criar_da_entidade || entidades.length > 0
  const podeAdaptar = (faixa: FaixaNaTela) => !faixa.pode_alterar && faixa.entidade_id === null && entidades.length > 0
  const procurado = semAcento(busca)
  const filtradas = resposta.dados.filter(
    (f) =>
      (aba === "todas" || (aba === "ativas") === f.ativo) &&
      (procurado === "" || semAcento(f.providencia).includes(procurado)),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtradas.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtradas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const mudar = (faixa: FaixaNaTela, ativo: boolean) => {
    api
      .alterarFaixa(faixa.id, { ativo })
      .then(() => {
        toast.success(`Faixa de ${faixa.dias} dias ${ativo ? "reativada" : "desativada"}`)
        carregar()
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const vazio =
    resposta.dados.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Providencia />
          </EmptyMedia>
          <EmptyTitle>Nenhuma faixa cadastrada</EmptyTitle>
          <EmptyDescription>{podeCriar ? "Cadastre a primeira faixa de providência." : "Só o Root cadastra."}</EmptyDescription>
        </EmptyHeader>
        {podeCriar && (
          <EmptyContent>
            <Button type="button" className="h-10" onClick={() => definirGaveta({ modo: "nova" })}>
              <Icones.Mais />
              Adicionar faixa
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nada encontrado</EmptyTitle>
          <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nada nesta situação."}</EmptyDescription>
        </EmptyHeader>
        {busca !== "" && (
          <EmptyContent>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      {cabecalho}
      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtradas.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          acaoNoTopo={
            podeCriar && (
              <Button type="button" className="h-10" onClick={() => definirGaveta({ modo: "nova" })}>
                <Icones.Mais />
                Adicionar faixa
              </Button>
            )
          }
          aoClicarNaLinha={(faixa) =>
            definirGaveta(faixa.pode_alterar ? { modo: "editar", faixa } : { modo: "adaptar", base: faixa })
          }
          linhaClicavel={(faixa) => faixa.pode_alterar || podeAdaptar(faixa)}
          abasNaBarra={
            <Abas
              nome="faixas"
              rotulo="Situação das faixas"
              classe="mb-0"
              abas={ABAS_DAS_FAIXAS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Providência" />
          <Column<FaixaNaTela> name="dias" label="Dias antes" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<FaixaNaTela>
            name="providencia"
            label="Providência"
            enableSorting={false}
            render={(valor) => <span className={cn("font-medium", MAIUSCULA_INLINE)}>{String(valor)}</span>}
          />
          <Column<FaixaNaTela> name="responsavel_rotulo" label="Responsável" enableSorting={false} />
          <Column<FaixaNaTela>
            name="escalonamento_rotulo"
            label="Se atrasar, escala para"
            enableSorting={false}
            render={(valor) => (valor === null ? "—" : String(valor))}
          />
          <Column<FaixaNaTela> name="vale_para" label="Vale para" enableSorting={false} render={(valor) => <span className={MAIUSCULA_INLINE}>{String(valor)}</span>} />
          <Column<FaixaNaTela> name="providencias_geradas" label="Já gerou" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<FaixaNaTela>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, faixa) => <Selo situacao={faixa.ativo ? "ativa" : "inativa"}>{faixa.ativo ? "Ativa" : "Desativada"}</Selo>}
          />
          <Acao<FaixaNaTela> label="Editar" icon={Icones.Anotacao} visivel={(faixa) => faixa.pode_alterar} onClick={(faixa) => definirGaveta({ modo: "editar", faixa })} />
          <Acao<FaixaNaTela>
            label="Adaptar para a entidade"
            icon={Icones.Mais}
            visivel={podeAdaptar}
            onClick={(faixa) => definirGaveta({ modo: "adaptar", base: faixa })}
          />
          <Acao<FaixaNaTela> label="Desativar" icon={Icones.Dispensar} visivel={(faixa) => faixa.pode_alterar && faixa.ativo} onClick={definirDesativando} />
          <Acao<FaixaNaTela> label="Reativar" icon={Icones.Reabrir} visivel={(faixa) => faixa.pode_alterar && !faixa.ativo} onClick={(faixa) => mudar(faixa, true)} />
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar a faixa de {desativando?.dias} dias?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Se desativada, para de gerar novas providências, e as já geradas continuam nos contratos. É possível reativar depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, false)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && definirGaveta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">{gaveta !== null ? tituloDaGaveta(gaveta) : "Faixa"}</SheetTitle>
          </SheetHeader>
          {gaveta !== null && (
            <FormularioDaFaixa
              key={gaveta.modo === "editar" ? gaveta.faixa.id : gaveta.modo === "adaptar" ? `adaptar-${gaveta.base.id}` : "nova"}
              gaveta={gaveta}
              resposta={resposta}
              entidades={entidades}
              aoCancelar={() => definirGaveta(null)}
              aoSalvar={(frase) => {
                toast.success(frase)
                definirGaveta(null)
                carregar()
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

export function FormularioDaFaixa({
  gaveta,
  resposta,
  entidades,
  aoCancelar,
  aoSalvar,
}: {
  gaveta: GavetaDaFaixa
  resposta: RespostaDasFaixas
  entidades: EntidadeNaLista[]
  aoCancelar: () => void
  aoSalvar: (frase: string) => void
}) {
  const inicial = gaveta.modo === "editar" ? gaveta.faixa : gaveta.modo === "adaptar" ? gaveta.base : null
  const editando = gaveta.modo === "editar"
  const opcoes = [
    ...(resposta.pode_criar_geral && gaveta.modo === "nova" ? [{ valor: "", rotulo: "Todas as entidades" }] : []),
    ...entidades.map((e) => ({ valor: String(e.id), rotulo: e.sigla })),
  ]
  const [dias, definirDias] = useState(inicial === null ? "" : String(inicial.dias))
  const [providencia, definirProvidencia] = useState(inicial?.providencia ?? "")
  const [responsavel, definirResponsavel] = useState(inicial?.responsavel ?? "gestor")
  const [escalonamento, definirEscalonamento] = useState(inicial?.escalonamento ?? "")
  const [valePara, definirValePara] = useState(opcoes[0]?.valor ?? "")
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [enviando, definirEnviando] = useState(false)
  const falta = editando
    ? faltaNoFormulario([["a providência", providencia]])
    : (faltaNoFormulario([
        ["quantos dias antes do término", dias],
        ["a providência", providencia],
      ]) ?? (opcoes.length === 0 ? "Falta uma entidade para a faixa valer" : null))

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    const escala = escalonamento === "" ? null : escalonamento
    const pedido =
      gaveta.modo === "editar"
        ? api
            .alterarFaixa(gaveta.faixa.id, { providencia, responsavel, escalonamento: escala })
            .then(() => `Faixa de ${gaveta.faixa.dias} dias salva`)
        : api
            .criarFaixa({
              dias: Number(dias),
              providencia,
              responsavel,
              escalonamento: escala,
              entidade_id: valePara === "" ? null : Number(valePara),
            })
            .then((faixa) => `Faixa de ${faixa.dias} dias criada para ${faixa.vale_para}`)
    pedido
      .then(aoSalvar)
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  const opcoesDeResponsavel = Object.entries(resposta.responsaveis).map(([chave, rotulo]) => (
    <option key={chave} value={chave}>
      {rotulo}
    </option>
  ))

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (falta === null && !enviando) salvar()
      }}
    >
      {gaveta.modo === "adaptar" && (
        <p className={cn("m-0 text-sm text-(--tinta-2)", MAIUSCULA)}>
          A faixa da entidade passa a valer no lugar da geral de {gaveta.base.dias} dias, só para a entidade escolhida.
        </p>
      )}
      {editando && (
        <p className={cn("m-0 text-sm text-(--tinta-2)", MAIUSCULA)}>
          {`Vale para ${gaveta.faixa.vale_para}. Os dias e a entidade não mudam; para outro prazo, adicione outra faixa.`}
        </p>
      )}
      {!editando && (
        <div>
          <RotuloDoCampo htmlFor="faixa-dias" obrigatorio>
            Dias antes do término
          </RotuloDoCampo>
          <Input
            id="faixa-dias"
            required
            type="number"
            min={0}
            value={dias}
            disabled={gaveta.modo === "adaptar"}
            onChange={(e) => definirDias(e.target.value)}
          />
        </div>
      )}
      <div>
        <RotuloDoCampo htmlFor="faixa-providencia" obrigatorio>
          Providência
        </RotuloDoCampo>
        <Textarea
          id="faixa-providencia"
          required
          rows={3}
          value={providencia}
          onChange={(e) => definirProvidencia(e.target.value)}
        />
      </div>
      <div>
        <RotuloDoCampo htmlFor="faixa-responsavel" obrigatorio>
          Responsável
        </RotuloDoCampo>
        <NativeSelect id="faixa-responsavel" required value={responsavel} onChange={(e) => definirResponsavel(e.target.value)}>
          {opcoesDeResponsavel}
        </NativeSelect>
      </div>
      <div>
        <label className={ROTULO_DE_CAMPO} htmlFor="faixa-escalonamento">
          Se atrasar, escala para
        </label>
        <NativeSelect id="faixa-escalonamento" value={escalonamento} onChange={(e) => definirEscalonamento(e.target.value)}>
          <option value="">Ninguém</option>
          {opcoesDeResponsavel}
        </NativeSelect>
      </div>
      {!editando && (
        <div>
          <label className={ROTULO_DE_CAMPO} htmlFor="faixa-vale">
            Vale para
          </label>
          <Escolha
            id="faixa-vale"
            valor={valePara}
            aoMudar={definirValePara}
            placeholderDaBusca="Buscar pela entidade"
            opcoes={opcoes}
          />
        </div>
      )}
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={falta !== null || enviando} title={falta ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}

type AbaDosTipos = "todos" | "ativos" | "desativados"

const ABAS_DOS_TIPOS: DescricaoDeAba<AbaDosTipos>[] = [
  { chave: "todos", rotulo: "Todos", dica: "Mostra todos os tipos de peça" },
  { chave: "ativos", rotulo: "Ativos", dica: "Mostra só os tipos que entram nas leituras do processo" },
  { chave: "desativados", rotulo: "Desativados", dica: "Mostra só os tipos desativados" },
]

const TIPOS_POR_PAGINA = 25

export function TiposDePeca() {
  const [resposta, definirResposta] = useState<RespostaDosTipos | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDosTipos>("todos")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(TIPOS_POR_PAGINA)
  const [editando, definirEditando] = useState<TipoNaTela | null>(null)
  const [desativando, definirDesativando] = useState<TipoNaTela | null>(null)

  const carregar = useCallback((termo: string) => {
    definirFalha(null)
    api
      .tiposDePeca(termo)
      .then(definirResposta)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])

  useEffect(() => {
    const espera = window.setTimeout(() => carregar(busca), busca === "" ? 0 : 250)
    return () => window.clearTimeout(espera)
  }, [busca, carregar])

  const cabecalho = (
    <CardHeader>
      <CardTitle>
        Tipos de peça do processo{" "}
        <ComDica dica="Diz em que parte da fiscalização o documento do ATHUS aparece. Se desativado, sai das próximas leituras do processo, e as peças que já entraram continuam com o papel que tinham." />
      </CardTitle>
    </CardHeader>
  )

  if (resposta === null) {
    return (
      <Card>
        {cabecalho}
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => carregar(busca)}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const filtrados = resposta.dados.filter((t) => aba === "todos" || (aba === "ativos") === t.ativo)
  const totalDePaginas = Math.max(1, Math.ceil(filtrados.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtrados.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const mudar = (tipo: TipoNaTela, ativo: boolean) => {
    api
      .alterarTipoDePeca(tipo.id, { ativo })
      .then(() => {
        toast.success(`Tipo ${tipo.descricao} ${ativo ? "reativado" : "desativado"}`)
        carregar(busca)
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const vazio = (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          {busca !== "" ? <Icones.Buscar /> : <Icones.TipoDePeca />}
        </EmptyMedia>
        <EmptyTitle>{busca !== "" ? "Nada encontrado" : "Nenhum tipo aqui"}</EmptyTitle>
        <EmptyDescription>
          {busca !== "" ? `Nada casa com "${busca}".` : resposta.dados.length === 0 ? "Os tipos chegam com a leitura do ATHUS." : "Nada nesta situação."}
        </EmptyDescription>
      </EmptyHeader>
      {busca !== "" && (
        <EmptyContent>
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
            Limpar a busca
          </Button>
        </EmptyContent>
      )}
    </Empty>
  )

  return (
    <Card>
      {cabecalho}
      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtrados.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          aoClicarNaLinha={definirEditando}
          linhaClicavel={(tipo) => tipo.pode_alterar}
          abasNaBarra={
            <Abas
              nome="tipos-de-peca"
              rotulo="Situação dos tipos de peça"
              classe="mb-0"
              abas={ABAS_DOS_TIPOS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Tipo, código ou observação" />
          <Column<TipoNaTela> name="codigo" label="Código" enableSorting={false} />
          <Column<TipoNaTela>
            name="descricao"
            label="Tipo"
            enableSorting={false}
            render={(valor) => <span className={cn("font-medium", MAIUSCULA_INLINE)}>{String(valor)}</span>}
          />
          <Column<TipoNaTela> name="papel_rotulo" label="Papel na fiscalização" enableSorting={false} />
          <Column<TipoNaTela> name="pecas_no_sistema" label="Peças" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<TipoNaTela>
            name="observacao"
            label="Observação"
            enableSorting={false}
            render={(valor) => (valor === null ? "—" : <span className={MAIUSCULA_INLINE}>{String(valor)}</span>)}
          />
          <Column<TipoNaTela>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, tipo) => <Selo situacao={tipo.ativo ? "ativa" : "inativa"}>{tipo.ativo ? "Ativo" : "Desativado"}</Selo>}
          />
          <Acao<TipoNaTela> label="Editar" icon={Icones.Anotacao} visivel={(tipo) => tipo.pode_alterar} onClick={definirEditando} />
          <Acao<TipoNaTela> label="Desativar" icon={Icones.Dispensar} visivel={(tipo) => tipo.pode_alterar && tipo.ativo} onClick={definirDesativando} />
          <Acao<TipoNaTela> label="Reativar" icon={Icones.Reabrir} visivel={(tipo) => tipo.pode_alterar && !tipo.ativo} onClick={(tipo) => mudar(tipo, true)} />
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar o tipo {desativando?.descricao}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Se desativado, sai das próximas leituras do processo, e as peças que já entraram continuam com o papel que tinham. É possível reativar depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, false)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={editando !== null} onOpenChange={(aberta) => !aberta && definirEditando(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">{editando !== null ? `Tipo: ${editando.descricao}` : "Tipo"}</SheetTitle>
          </SheetHeader>
          {editando !== null && (
            <FormularioDoTipo
              key={editando.id}
              tipo={editando}
              papeis={resposta.papeis}
              aoCancelar={() => definirEditando(null)}
              aoSalvar={(mudanca) =>
                api.alterarTipoDePeca(editando.id, mudanca).then(() => {
                  toast.success(`Tipo ${editando.descricao} salvo`)
                  definirEditando(null)
                  carregar(busca)
                })
              }
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

export function FormularioDoTipo({
  tipo,
  papeis,
  aoCancelar,
  aoSalvar,
}: {
  tipo: TipoNaTela
  papeis: Record<string, string>
  aoCancelar: () => void
  aoSalvar: (mudanca: { papel: string; observacao: string | null }) => Promise<unknown> | void
}) {
  const [papel, definirPapel] = useState(tipo.papel)
  const [observacao, definirObservacao] = useState(tipo.observacao ?? "")
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [enviando, definirEnviando] = useState(false)
  const falta = faltaNoFormulario([["o papel na fiscalização", papel]])

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    Promise.resolve(aoSalvar({ papel, observacao: observacao.trim() === "" ? null : observacao }))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (falta === null && !enviando) salvar()
      }}
    >
      <p className="m-0 text-sm text-(--tinta-2)">
        Código no ATHUS: <strong className="font-semibold text-(--tinta)">{tipo.codigo}</strong>
      </p>
      <div>
        <RotuloDoCampo htmlFor="tipo-papel" obrigatorio>
          Papel na fiscalização
        </RotuloDoCampo>
        <NativeSelect id="tipo-papel" required value={papel} onChange={(e) => definirPapel(e.target.value)}>
          {Object.entries(papeis).map(([chave, rotulo]) => (
            <option key={chave} value={chave}>
              {rotulo}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div>
        <label className={ROTULO_DE_CAMPO} htmlFor="tipo-observacao">
          Observação
        </label>
        <Textarea id="tipo-observacao" rows={3} value={observacao} onChange={(e) => definirObservacao(e.target.value)} />
      </div>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={falta !== null || enviando} title={falta ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}
